import moment from 'moment';
import {
  SettingsMap,
  MappedSettings,
  SettingsType,
  MappedSection,
  ComponentMap
} from './dashboardSettingsTypes';


function formatDecimal(value: number, fractionDigits: number) {
    value = Math.round(value * 10 ** fractionDigits) / 10 ** fractionDigits;

    if (value === -0) value = 0;

    return (value as number).toLocaleString(undefined, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
}


export function getValue(
  componentMap: ComponentMap,
  valuesSource: any,
  _default?: string,
  currencyConfig?: Currency,
  noFormat?: boolean,
) {
  const numericFormats = [
    'currency',
    'decimal',
    'integer',
    '100percentage',
    'percentage',
    'ratio',
  ];
  let fields: string[] = [];
  fields = componentMap.fieldName.split('.');

  let value = valuesSource;
  for (const field of fields) {
    if (value === null) break;
    value = value[field];
  }

  if (value === undefined) {
    console.log('Value source of the error below:', valuesSource);
    throw new Error(`Value of ${componentMap.fieldName} is undefined.`);
  }
  if (noFormat) {
    if (value === null || value === '') {
      if (_default !== undefined) return _default;
      if (numericFormats.find(f => componentMap.fieldType.startsWith(f))) return 0;
      if (componentMap.fieldType === 'boolean') return false;
    }
    return value;
  }

  if (value === null || value === '') return _default || '';  // zero should be formatted


  if (componentMap.fieldType === 'boolean') {
    return value ? 'yes' : 'no';

  } else if (componentMap.fieldType.startsWith('currency')) {
    if (!currencyConfig) throw new Error(`Need currencyConfig argument to display field ${componentMap.fieldName}`);

    // ex: => USD converted to Usd | so converted value = {fieldName}{finalFieldSuffix}
    const finalFieldSuffix = (currencyConfig?.code.charAt(0) || '').toUpperCase() + (currencyConfig?.code.slice(1) || '').toLowerCase()

    // convert: field-ex: amountUsd
    // NOTE: All mentioned currency conversion `fieldName` only have a single level depth search (i.e fields.length === 1)
    const convertedValue = valuesSource[`${fields[0]}${finalFieldSuffix}`]

    // replace existing value with converted value
    if (convertedValue !== undefined) {
      value = convertedValue
    }

    const fractionDigits = parseInt(componentMap.fieldType.substring(8));
    return `${currencyConfig?.sign}${formatDecimal(value, fractionDigits)}`;

  } else if (componentMap.fieldType === 'date') {
    return (value as moment.Moment).format('MMM D, YYYY');

  } else if (componentMap.fieldType.startsWith('decimal')) {
    const fractionDigits = parseInt(componentMap.fieldType.substring(7));
    return formatDecimal(value, fractionDigits);

  } else if (componentMap.fieldType === 'integer') {
    return formatDecimal(value, 0);

  } else if (componentMap.fieldType.startsWith('100percentage')) {
    const fractionDigits = parseInt(componentMap.fieldType.substring(13));
    return `${formatDecimal(value * 100, fractionDigits)}%`;

  } else if (componentMap.fieldType.startsWith('percentage')) {
    const fractionDigits = parseInt(componentMap.fieldType.substring(10));
    return `${formatDecimal(value, fractionDigits)}%`;

  } else if (componentMap.fieldType.startsWith('ratio')) {
    const fractionDigits = parseInt(componentMap.fieldType.substring(5));
    return `${formatDecimal(value, fractionDigits)}x`;

  } else if (componentMap.fieldType.startsWith('string')) {
    return `${value}`;

  } else if (componentMap.fieldType === 'url') {
    const prefix = 'https://';
    return value.substr(0, prefix.length) !== prefix ? prefix + value : value;

  } else {
    throw new Error(`Unknown field type: ${componentMap.fieldType}`);
  }
}

export function makeMappedSettings(settings: SettingsType, settingsMap: SettingsMap) {
  let mappedSettings: MappedSettings = {
    dashboardType: settings.dashboardType,
    sections: []
  };
  for (const sectionName in settingsMap) {
    const section = settings.sections.find(s => s.name === sectionName);
    if (!section) throw new Error(`Section "${sectionName}" not found in received settings`);
    const sectionMap = settingsMap[sectionName];

    let mappedSection: MappedSection = {
      ...section,
      ...sectionMap,
      components: []
    };

    for (const componentName in sectionMap.components) {
      const component = section.components.find(c => c.name === componentName);
      if (!component) throw new Error(`Component "${componentName}" not found in received settings`);
      const componentMap = sectionMap.components[componentName];

      mappedSection.components.push({
        ...componentMap,
        ...component,
      });
    }
    mappedSection.components = mappedSection.components
      .sort((a, b) => a.order - b.order);
    if (mappedSection.components.length) {
      mappedSection.checked = !!mappedSection.components.filter(c => c.checked).length;
    }
    mappedSettings.sections.push(mappedSection);
  }
  mappedSettings.sections = mappedSettings.sections
    .sort((a, b) => a.order - b.order);
  return mappedSettings;
}